import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';

interface InvoiceForm {
  studioName: string;
  ownerVatNumber: string;
  ownerFiscalCode: string;
  ownerAddress: string;
  ownerCity: string;
  ownerIban: string;
}

export function InvoiceSettingsSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState<InvoiceForm>({
    studioName: '', ownerVatNumber: '', ownerFiscalCode: '',
    ownerAddress: '', ownerCity: '', ownerIban: '',
  });
  const [loaded, setLoaded] = useState(false);

  const { data: invoiceData } = useQuery({
    queryKey: ['user-settings-invoice'],
    queryFn: () => apiGet<InvoiceForm>('/user-settings/invoice'),
  });

  useEffect(() => {
    if (invoiceData && !loaded) {
      setForm({
        studioName:      invoiceData.studioName      ?? '',
        ownerVatNumber:  invoiceData.ownerVatNumber  ?? '',
        ownerFiscalCode: invoiceData.ownerFiscalCode ?? '',
        ownerAddress:    invoiceData.ownerAddress    ?? '',
        ownerCity:       invoiceData.ownerCity       ?? '',
        ownerIban:       invoiceData.ownerIban       ?? '',
      });
      setLoaded(true);
    }
  }, [invoiceData, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/invoice', {
      studioName:      form.studioName      || null,
      ownerVatNumber:  form.ownerVatNumber  || null,
      ownerFiscalCode: form.ownerFiscalCode || null,
      ownerAddress:    form.ownerAddress    || null,
      ownerCity:       form.ownerCity       || null,
      ownerIban:       form.ownerIban       || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-invoice'] });
      toast.success('Dati fatturazione salvati');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const field = (label: string, key: keyof InvoiceForm, ph?: string) => (
    <Input
      label={label}
      value={form[key]}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      placeholder={ph}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
        Informazioni che compaiono come emittente sulle fatture generate.
      </p>
      {field('Nome studio / Ragione sociale', 'studioName', 'es. Studio Fotografico Rossi')}
      <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Partita IVA', 'ownerVatNumber', 'IT01234567890')}
        {field('Codice fiscale', 'ownerFiscalCode')}
      </div>
      <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        {field('Indirizzo', 'ownerAddress')}
        {field('Città', 'ownerCity')}
      </div>
      {field('IBAN', 'ownerIban', 'IT60X0542811101000000123456')}
      <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} style={{ alignSelf: 'flex-start' }}>
        Salva dati fatturazione
      </Button>
    </div>
  );
}
